import { useAuth } from '@/context/AuthContext';
import { useImpersonation } from '@/hooks/useImpersonation';
import { api } from '@/lib/trpc/react';
import type { TourType } from '@/types/tour';
import { nanoid } from 'nanoid';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { tourConfig } from './tourConfig';

interface TourContextType {
  activeTour: TourType | null;
  currentStep: number;
  tourQueue: TourType[];
  startTour: (tourId: TourType) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  isTourCompleted: (tourId: TourType) => boolean;
  resetTours: () => void;
  checkTourVisibility: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTour, setActiveTour] = useState<TourType | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourQueue, setTourQueue] = useState<TourType[]>([]);

  const { isAdmin } = useImpersonation();
  const utils = api.useUtils();

  const { isLoggedIn, user } = useAuth();
  const { data: tours } = api.tours.byUser.useQuery(undefined, {
    enabled: isLoggedIn,
  });
  const { mutate: completeTourMutation } = api.tours.completeTour.useMutation({
    onMutate: () => {
      utils.tours.byUser.setData(undefined, (old) => {
        if (!old) return old;
        return [
          ...old,
          {
            id: nanoid(),
            userId: user?.id ?? '',
            createdAt: new Date().toISOString(),
            type: activeTour ?? '',
            completed: true,
          },
        ];
      });
    },
  });
  const { mutate: resetToursMutation } = api.tours.resetTours.useMutation({
    onSuccess: () => {
      utils.tours.byUser.invalidate();
    },
  });

  const isTourStepVisible = useCallback(
    (tourId: TourType, stepIndex: number) => {
      const tour = tourConfig[tourId];
      if (!tour || !tour.steps[stepIndex]) return false;

      const step = tour.steps[stepIndex];
      const target = document.querySelector(step.target);
      return !!target;
    },
    [],
  );

  const processNextTour = useCallback(() => {
    if (tourQueue.length === 0 || activeTour) {
      return;
    }

    const cleanQueue = tourQueue.filter((tourId) => {
      const isCompleted = tours?.some(
        (tour) => tour.type === tourId && tour.completed,
      );
      if (isCompleted) {
        return false;
      }
      return true;
    });

    if (cleanQueue.length !== tourQueue.length) {
      setTourQueue(cleanQueue);
    }

    const nextTour = cleanQueue.find((tourId) => {
      return isTourStepVisible(tourId, 0);
    });

    if (nextTour) {
      setActiveTour(nextTour);
      setCurrentStep(0);
      setTourQueue((prev) => prev.filter((id) => id !== nextTour));
    }
  }, [tourQueue, activeTour, tours, isTourStepVisible]);

  const startTour = useCallback(
    (tourId: TourType) => {
      if (isAdmin) return;

      const isCompleted = tours?.some(
        (tour) => tour.type === tourId && tour.completed,
      );

      if (isCompleted || activeTour === tourId || tourQueue.includes(tourId)) {
        return;
      }

      if (!activeTour) {
        const canShow = isTourStepVisible(tourId, 0);
        if (canShow) {
          setActiveTour(tourId);
          setCurrentStep(0);
          return;
        }
      }

      setTourQueue((prev) => {
        if (prev.includes(tourId)) {
          return prev;
        }
        return [...prev, tourId];
      });
    },
    [isAdmin, tours, activeTour, tourQueue, isTourStepVisible],
  );

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const skipTour = useCallback(() => {
    if (activeTour && !isAdmin) {
      completeTourMutation({ type: activeTour });
    }
    setActiveTour(null);
    setCurrentStep(0);
    setTimeout(processNextTour, 100);
  }, [activeTour, isAdmin, completeTourMutation, processNextTour]);

  const completeTour = useCallback(() => {
    if (activeTour && !isAdmin) {
      completeTourMutation({ type: activeTour });
      setActiveTour(null);
      setCurrentStep(0);
      setTimeout(processNextTour, 100);
    }
  }, [activeTour, isAdmin, completeTourMutation, processNextTour]);

  const resetTours = useCallback(() => {
    resetToursMutation();
    setActiveTour(null);
    setCurrentStep(0);
    setTourQueue([]);
  }, [resetToursMutation]);

  const isTourCompleted = useCallback(
    (tourId: TourType) =>
      tours?.some((tour) => tour.type === tourId && tour.completed) ?? false,
    [tours],
  );

  const checkTourVisibility = useCallback(() => {
    if (activeTour) {
      const isVisible = isTourStepVisible(activeTour, currentStep);
      if (!isVisible) {
        setActiveTour(null);
        setCurrentStep(0);

        setTimeout(processNextTour, 100);
      }
    }
  }, [activeTour, currentStep, isTourStepVisible, processNextTour]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTour) {
        const isVisible = isTourStepVisible(activeTour, currentStep);
        if (!isVisible) {
          setActiveTour(null);
          setCurrentStep(0);
          return;
        }
      }

      if (!activeTour && tourQueue.length > 0) {
        processNextTour();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [
    activeTour,
    currentStep,
    tourQueue.length,
    processNextTour,
    isTourStepVisible,
  ]);

  const contextValue = useMemo(
    () => ({
      activeTour,
      tourQueue,
      currentStep,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      isTourCompleted,
      resetTours,
      checkTourVisibility,
    }),
    [
      activeTour,
      tourQueue,
      currentStep,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      isTourCompleted,
      resetTours,
      checkTourVisibility,
    ],
  );

  return (
    <TourContext.Provider value={contextValue}>{children}</TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
